import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { isNotNil } from 'ramda';

import Icon, { IconName } from '~/components/Icon';
import { useDailyGameStatus } from '~/hooks/useDailyGameStatus';
import { formatTimeLeft } from '~/utils/formatTimeLeft';
import { cn } from '~/utils/cn';

interface DailyRiddleTimerProps {
  /** Drop the pill container so the timer can sit inside a Card description. */
  inline?: boolean;
}

export default function DailyRiddleTimer({ inline }: DailyRiddleTimerProps) {
  const { isLoading, error, dailyGameStatus } = useDailyGameStatus();

  const [timeLeft, setTimeLeft] = useState<string | null>(null);
  const iconName: Record<string, IconName> = {
    PANORAMA: 'panorama',
    SCREENSHOT: 'image',
    SOUND: 'sound',
    VIDEO: 'video',
    MODEL: 'model'
  };

  useEffect(() => {
    const targetTime = new Date(
      dailyGameStatus?.nextDailyRiddleDate || new Date()
    ).getTime();

    const updateTimeLeft = () => {
      const diffMs = targetTime - Date.now();
      setTimeLeft(formatTimeLeft(diffMs));
    };

    updateTimeLeft();
    const timer = setInterval(updateTimeLeft, 1000);

    // Add visibility change listener to update time when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateTimeLeft();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dailyGameStatus]);

  const isReady = !isLoading && dailyGameStatus && !error;
  const showTimer = dailyGameStatus?.playedDailyRiddle && isNotNil(timeLeft);

  const fadeInProps = {
    initial: { opacity: 0, y: 2 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.3, ease: 'easeOut' }
  } as const;

  const containerClassName = cn(
    inline
      ? 'flex w-fit items-center justify-start gap-2 normal-case'
      : 'bottom-10 right-10 z-10 mb-2 flex h-8 w-fit flex-col gap-3 rounded-lg bg-black/25 px-2 py-1 text-center normal-case backdrop-blur-[32px] max-md:ml-11 justify-center items-center'
  );

  if (!isReady) {
    return (
      <div
        className={cn(inline ? 'h-[22px] w-24' : 'bottom-10 mb-2 h-8 w-24')}
      />
    );
  }

  return (
    <AnimatePresence mode="wait">
      {showTimer ? (
        <motion.div
          key="timer"
          data-cy="next-daily-riddle-timer"
          {...fadeInProps}
          className={containerClassName}
        >
          <span className="text-sm">
            New riddle in{' '}
            <span className="inline-block w-15 whitespace-nowrap text-left">
              {timeLeft}
            </span>
          </span>
        </motion.div>
      ) : (
        <motion.div
          key="riddle-type"
          data-cy="current-daily-riddle-type"
          {...fadeInProps}
          className={containerClassName}
        >
          <span className="flex w-fit flex-row items-center gap-2 text-sm">
            <Icon
              name={
                iconName[dailyGameStatus?.currentDailyRiddleType] || 'question'
              }
              size={inline ? 4 : 6}
            />
            <span>
              {dailyGameStatus?.currentDailyRiddleType &&
                dailyGameStatus.currentDailyRiddleType.charAt(0).toUpperCase() +
                  dailyGameStatus.currentDailyRiddleType.slice(1).toLowerCase()}
            </span>
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
